import type { CSSProperties, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '@artosera/ui';
import { usePeut } from '../../../../auth/usePeut.js';
import { listerDepots } from '../../../../lib/depots.js';
import { formaterDate } from '../../../../lib/format.js';
import {
  ambigusRestants,
  depotReinventorie,
  etapesImages,
  rapprocherLot,
} from '../../../../lib/images.js';
import { morceauxLances } from '../../../../lib/reglesEdition.js';
import { lotEnTravail, type LotReprise } from '../../../../lib/reprise.js';
import pageStyles from '../../../../styles/page.module.css';
import correspondance from '../correspondance/Correspondance.module.css';
import { cleDepots } from '../DepotsBloc.js';
import { cleLots } from '../LotsBloc.js';
import { TON_ETAT_LOT, messageReprise } from '../messages.js';
import { MorceauxEssai } from '../MorceauxEssai.js';
import reprise from '../Reprise.module.css';
import { RevueRapprochement } from './RevueRapprochement.js';

const COLONNES_ETAPES = { '--etapes-colonnes': 5 } as CSSProperties;

/**
 * La fiche d'un lot d'images tiré d'un dépôt : ses étapes, le rapprochement
 * avec les œuvres, la revue (décisions comprises), puis l'essai et le
 * lancement des morceaux. Le lot parent ne se lance jamais : tant qu'un ambigu
 * reste, il n'a même pas de morceaux.
 */
export function LotImages({
  idGalerie,
  lot,
  onLot,
  onEssaiLance,
}: {
  idGalerie: string;
  lot: LotReprise;
  onLot: (lot: LotReprise) => void;
  onEssaiLance: () => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const client = useQueryClient();
  const [erreur, setErreur] = useState<string | null>(null);
  const peutAgir = peut('console:run-import');
  const enTravail = lotEnTravail(lot);
  const rapproche = lot.rapprochement?.fait === true;
  const ambigus = ambigusRestants(lot);
  const lances = morceauxLances(lot) > 0;
  const morceaux = lot.morceaux?.liste ?? [];

  const depots = useQuery({
    queryKey: [...cleDepots(idGalerie), 'pour-un-lot'],
    queryFn: () => listerDepots(idGalerie),
  });
  const depot = depots.data?.find((candidat) => candidat.id === lot.depotId);

  const apres = (suite: LotReprise): void => {
    setErreur(null);
    onLot(suite);
    void client.invalidateQueries({ queryKey: cleLots(idGalerie) });
  };
  const rapprocher = useMutation({
    mutationFn: () => rapprocherLot(idGalerie, lot.id),
    onSuccess: apres,
    onError: (cause) => {
      setErreur(messageReprise(t, cause, 'images'));
      void client.invalidateQueries({
        queryKey: ['console', 'galerie', idGalerie, 'reprise', 'lot', lot.id],
      });
    },
  });

  const etapes = etapesImages(lot);
  const detail = (etape: string): string | null => {
    if (morceaux.length === 0) return null;
    if (etape === 'essai')
      return t('correspondance.etapes.detailEssai', {
        faits: morceaux.filter((morceau) => morceau.etat !== 'depose').length,
        total: morceaux.length,
      });
    if (etape === 'a_lancer')
      return t('correspondance.etapes.detailLancement', {
        faits: morceauxLances(lot),
        total: morceaux.length,
      });
    return null;
  };

  const refus = !enTravail && lot.travail?.erreur ? lot.travail.erreur : null;

  return (
    <>
      <div className={pageStyles.panneau}>
        <div className={pageStyles.sectionTitre}>
          <h2>{t('images.fiche.titre')}</h2>
          <Badge tone={TON_ETAT_LOT[lot.etat]}>{t(`reprise.etats.${lot.etat}`)}</Badge>
        </div>
        <div className={reprise.pile}>
          <p className={reprise.discret}>
            {t('reprise.fiche.meta', {
              date: formaterDate(lot.creeLe, '—'),
              operateur: lot.operateur,
            })}
          </p>
          <ol
            className={correspondance.etapes}
            style={COLONNES_ETAPES}
            aria-label={t('correspondance.etapes.aria')}
          >
            {etapes.map(({ etape, etat }) => {
              const complement = detail(etape);
              return (
                <li
                  key={etape}
                  className={correspondance.etape}
                  data-etat={etat}
                  aria-current={etat === 'courante' ? 'step' : undefined}
                >
                  <span className={correspondance.etapeNom}>{t(`images.etapes.${etape}`)}</span>
                  <span className={correspondance.etapeEtat}>
                    {t(`correspondance.etapes.etat.${etat}`)}
                  </span>
                  {complement ? (
                    <span className={correspondance.etapeDetail}>{complement}</span>
                  ) : null}
                </li>
              );
            })}
          </ol>

          <h3 className={reprise.sousTitre}>{t('images.fiche.lecture')}</h3>
          <dl className={reprise.details}>
            <div>
              <dt>{t('images.fiche.depot')}</dt>
              <dd>{depot ? depot.libelle || t('reprise.depots.sansNom') : '—'}</dd>
            </div>
            <div>
              <dt>{t('images.fiche.dossier')}</dt>
              <dd>
                {lot.options.dossier ? (
                  <span className={reprise.chemin}>{lot.options.dossier}</span>
                ) : (
                  t('images.fiche.toutLeDepot')
                )}
              </dd>
            </div>
            <div>
              <dt>{t('images.fiche.principale')}</dt>
              <dd>
                {lot.options.remplacerPrincipale
                  ? t('images.fiche.principaleRemplacee')
                  : t('images.fiche.principaleGardee')}
              </dd>
            </div>
          </dl>

          {enTravail ? (
            <p className={reprise.discret} aria-live="polite">
              {t('images.fiche.rapprochementEnCours')}
            </p>
          ) : null}
          {refus ? (
            <p className={reprise.erreur} role="alert">
              {depotReinventorie(refus)
                ? t('images.erreurs.depot_reinventorie')
                : t('images.fiche.renonce', { message: refus })}
            </p>
          ) : null}
          {erreur ? (
            <p className={reprise.erreur} role="alert">
              {erreur}
            </p>
          ) : null}

          {peutAgir ? (
            <div className={reprise.actions}>
              <Button
                variant={rapproche ? 'secondary' : 'primary'}
                loading={rapprocher.isPending || enTravail}
                disabled={lances || (refus !== null && depotReinventorie(refus))}
                onClick={() => rapprocher.mutate()}
              >
                {rapproche ? t('images.fiche.rapprocherDeNouveau') : t('images.fiche.rapprocher')}
              </Button>
            </div>
          ) : null}
          <p className={reprise.discret}>
            {lances
              ? t('images.fiche.morceauxLances')
              : rapproche
                ? t('images.fiche.rapprocherDeNouveauAide')
                : t('images.fiche.rapprocherAide')}
          </p>
        </div>
      </div>

      {rapproche ? (
        <section className={pageStyles.panneau} aria-labelledby="images-revue">
          <div className={pageStyles.sectionTitre}>
            <h2 id="images-revue">{t('images.revue.titre')}</h2>
          </div>
          <div className={reprise.pile}>
            <p className={reprise.texte}>{t('images.revue.texte')}</p>
            <RevueRapprochement
              idGalerie={idGalerie}
              lot={lot}
              peutAgir={peutAgir && !lances}
              onLot={apres}
            />
          </div>
        </section>
      ) : null}

      {rapproche ? (
        <section className={pageStyles.panneau} aria-labelledby="images-essai">
          <div className={pageStyles.sectionTitre}>
            <h2 id="images-essai">{t('images.essaiLancement.titre')}</h2>
          </div>
          <div className={reprise.pile}>
            {ambigus > 0 ? (
              <>
                <p className={reprise.avertissement} role="note">
                  {t('images.essaiLancement.bloque', { count: ambigus })}
                </p>
                {peutAgir ? (
                  <div className={reprise.actions}>
                    <Button variant="secondary" disabled>
                      {t('images.essai.essayer')}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <p className={reprise.texte}>{t('images.essaiLancement.texte')}</p>
                <MorceauxEssai
                  idGalerie={idGalerie}
                  lot={lot}
                  peutAgir={peutAgir}
                  actif={lot.etat === 'rapproche' && morceaux.length > 0}
                  textes="images"
                  onLot={apres}
                  onEssaiLance={onEssaiLance}
                />
                {morceaux.length === 0 ? (
                  <p className={reprise.discret}>{t('images.essaiLancement.aucunMorceau')}</p>
                ) : null}
                {lot.morceaux?.compteurs.converties ? (
                  <p className={reprise.discret}>
                    {t('images.essaiLancement.converties', {
                      count: lot.morceaux.compteurs.converties,
                    })}
                  </p>
                ) : null}
              </>
            )}
          </div>
        </section>
      ) : null}
    </>
  );
}
